import { useState, useEffect } from 'react';
import { Search, X, SlidersHorizontal, Users as UsersIcon, Store, Lock, Plus } from 'lucide-react';
import api from '../lib/api';
import PlanCard from '../components/PlanCard';
import FollowButton from '../components/FollowButton';
import PartnerCard from '../components/PartnerCard';
import CommunityLockedSheet from '../components/CommunityLockedSheet';
import CommunityCreateSheet from '../components/CommunityCreateSheet';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = ['coffee', 'fitness', 'food', 'social', 'wellness', 'sports', 'culture', 'networking'];

// Q34.1 — Partner discovery taxonomy
const PARTNER_TYPES = [
  'coffee', 'restaurant', 'fitness', 'wellness', 'coworking', 'arts',
  'entertainment', 'hotel', 'outdoor', 'nightlife', 'sports', 'education',
  'networking', 'shopping',
];
const PARTNER_SORTS = [
  { key: 'popular', label: 'Most popular' },
  { key: 'most_groups', label: 'Most groups hosted' },
  { key: 'newest', label: 'Newest' },
  { key: 'most_offers', label: 'Most offers' },
  { key: 'top_rated', label: 'Highest rated' },
];

function getInitials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

export default function Explore() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('plans');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [plans, setPlans] = useState([]);
  const [people, setPeople] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnerType, setPartnerType] = useState('');
  const [partnerSort, setPartnerSort] = useState('popular');
  const [loading, setLoading] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [showLocked, setShowLocked] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    const search = async () => {
      setLoading(true);
      try {
        if (tab === 'plans') {
          const params = new URLSearchParams();
          if (query) params.set('q', query);
          if (category) params.set('category', category);
          const { data } = await api.get(`/explore/plans?${params}`);
          setPlans(data);
        } else if (tab === 'people') {
          const { data } = await api.get(`/explore/people?q=${query}`);
          setPeople(data);
        } else if (tab === 'communities') {
          const params = new URLSearchParams();
          if (query) params.set('q', query);
          if (category) params.set('category', category);
          const { data } = await api.get(`/communities/discover/list?${params}`);
          setCommunities(data || []);
        } else if (tab === 'partners') {
          const params = new URLSearchParams();
          if (query) params.set('q', query);
          if (partnerType) params.set('business_types', partnerType);
          if (partnerSort) params.set('sort', partnerSort);
          const { data } = await api.get(`/businesses/discover?${params}`);
          setPartners(data || []);
        }
      } catch { /* network — leave previous results */ }
      setLoading(false);
    };
    const debounce = setTimeout(search, 300);
    return () => clearTimeout(debounce);
  }, [tab, query, category, partnerType, partnerSort]);

  // Q34 — preload eligibility once, refresh when we open Communities
  useEffect(() => {
    if (tab !== 'communities') return;
    api.get('/communities/eligibility').then(r => setEligibility(r.data)).catch(() => setEligibility(null));
  }, [tab]);

  const openCreateCommunity = () => {
    if (eligibility?.eligible) setShowCreate(true);
    else setShowLocked(true);
  };

  return (
    <div className="content-area animate-fade-in" data-testid="explore-page">
      <div className="px-4 pt-5 pb-3">
        <h1 className="text-2xl font-bold tracking-tight text-[#111111]">Explore</h1>
      </div>

      {/* Search */}
      <div className="px-4 mb-4">
        <div className="relative">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9B9EA3]" />
          <input
            data-testid="explore-search"
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={
              tab === 'plans' ? 'Search plans, venues, cities...'
              : tab === 'people' ? 'Search people, usernames...'
              : tab === 'communities' ? 'Search communities...'
              : 'Search partners, offers, areas...'
            }
            className="w-full bg-[#FFFFFF] text-[#111111] rounded-2xl pl-10 pr-10 py-3 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 placeholder-[#B0B3B8]"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2">
              <X size={16} className="text-[#9B9EA3]" />
            </button>
          )}
        </div>
      </div>

      {/* 4-tab toggle — Q34 */}
      <div className="px-4 mb-4 flex gap-2 overflow-x-auto hide-scrollbar">
        {['plans', 'people', 'communities', 'partners'].map(t => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`flex-shrink-0 px-5 py-2 rounded-full text-sm font-medium transition-all ${
              tab === t
                ? 'bg-[#111111] text-white'
                : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Category pills (Plans / Communities) */}
      {(tab === 'plans' || tab === 'communities') && (
        <div className="px-4 mb-4 flex gap-2 overflow-x-auto hide-scrollbar">
          <button
            data-testid="filter-all"
            onClick={() => setCategory('')}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
              !category ? 'bg-white/10 text-[#111111]' : 'bg-[#F1F0EA] text-[#9B9EA3] border border-black/[0.06]'
            }`}
          >
            All
          </button>
          {CATEGORIES.map(c => (
            <button
              key={c}
              data-testid={`filter-${c}`}
              onClick={() => setCategory(category === c ? '' : c)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${
                category === c ? 'bg-white/10 text-[#111111]' : 'bg-[#F1F0EA] text-[#9B9EA3] border border-black/[0.06]'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Partner-tab filters — type pills + sort dropdown */}
      {tab === 'partners' && (
        <div className="px-4 mb-3 space-y-2">
          <div className="flex gap-2 overflow-x-auto hide-scrollbar">
            <button
              data-testid="partner-type-all"
              onClick={() => setPartnerType('')}
              className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-bold capitalize ${
                !partnerType ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'
              }`}
            >All</button>
            {PARTNER_TYPES.map(t => (
              <button
                key={t}
                data-testid={`partner-type-${t}`}
                onClick={() => setPartnerType(partnerType === t ? '' : t)}
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-bold capitalize ${
                  partnerType === t ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'
                }`}
              >{t}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={13} className="text-[#9B9EA3]" />
            <select
              data-testid="partner-sort"
              value={partnerSort}
              onChange={e => setPartnerSort(e.target.value)}
              className="text-xs font-bold bg-white border border-black/[0.06] rounded-full px-3 py-1 text-[#111]"
            >
              {PARTNER_SORTS.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Communities — create CTA (gated) */}
      {tab === 'communities' && (
        <div className="px-4 mb-3">
          <button
            data-testid="communities-create-btn"
            onClick={openCreateCommunity}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-dashed text-sm font-extrabold transition-colors
              ${eligibility?.eligible
                ? 'bg-[#B6E600]/10 border-[#B6E600]/40 text-[#3F5200]'
                : 'bg-[#FAFAF7] border-black/[0.08] text-[#6B6E73]'}`}
          >
            {eligibility?.eligible
              ? <><Plus size={15} strokeWidth={2.8} /> Create a community</>
              : <><Lock size={13} strokeWidth={2.8} /> Unlock Community Leader to create</>}
          </button>
        </div>
      )}

      {/* Results */}
      <div className="px-4 pb-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="skeleton h-[100px] rounded-2xl" />)}
          </div>
        ) : tab === 'plans' ? (
          <div className="space-y-3 stagger-children">
            {plans.map(plan => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
            {plans.length === 0 && (
              <div className="text-center py-16 px-6">
                <div className="w-16 h-16 rounded-full bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-4">
                  <SlidersHorizontal size={28} className="text-[#3F5200]" />
                </div>
                <p className="text-base font-extrabold text-[#111] mb-1">
                  {query ? 'Nothing matches that search' : 'No plans yet here'}
                </p>
                <p className="text-sm text-[#6B6E73] leading-relaxed mb-4 max-w-xs mx-auto">
                  {query
                    ? 'Try a different keyword, area, or date.'
                    : 'Be the first to host something this weekend ✨'}
                </p>
                <button
                  onClick={() => navigate('/create')}
                  data-testid="explore-plans-empty-cta"
                  className="px-5 py-2.5 rounded-full bg-[#B6E600] text-[#111] text-sm font-extrabold active:scale-95 transition"
                >
                  Host a plan
                </button>
              </div>
            )}
          </div>
        ) : tab === 'people' ? (
          <div className="space-y-2 stagger-children">
            {people.map(person => (
              <div
                key={person.id}
                data-testid={`person-${person.id}`}
                className="flex items-start gap-3 p-3 bg-[#FFFFFF] rounded-2xl border border-black/[0.06] animate-fade-in"
              >
                <div
                  className="flex-shrink-0 cursor-pointer"
                  onClick={() => navigate(`/user/${person.id}`)}
                >
                  {person.profile_photo ? (
                    <img loading="lazy" decoding="async" src={person.profile_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#F1F0EA] flex items-center justify-center text-sm font-bold text-[#9B9EA3]">
                      {getInitials(person.display_name)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/user/${person.id}`)}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="font-extrabold text-sm text-[#111111] truncate max-w-[180px]">{person.display_name}</p>
                    {(person.badges_computed || []).slice(0, 2).map(b => (
                      <span key={b.slug} data-testid={`badge-${b.slug}`} className="text-[9px] uppercase tracking-wider font-extrabold bg-[#F1F0EA] text-[#3F5200] px-1.5 py-0.5 rounded-full">
                        {b.emoji} {b.label}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-[#9B9EA3]">@{person.username}</p>
                  {person.bio && <p className="text-xs text-[#6B6E73] mt-0.5 truncate">{person.bio}</p>}
                  {person.mutuals?.count > 0 && (
                    <div data-testid={`mutuals-${person.id}`} className="flex items-center gap-1.5 mt-1.5">
                      <div className="flex -space-x-1.5">
                        {(person.mutuals.sample || []).slice(0, 2).map(m => (
                          m.profile_photo
                            ? <img loading="lazy" decoding="async" key={m.id} src={m.profile_photo} alt="" className="w-4 h-4 rounded-full border border-white object-cover" />
                            : <div key={m.id} className="w-4 h-4 rounded-full border border-white bg-[#F1F0EA] text-[7px] flex items-center justify-center font-bold text-[#9B9EA3]">{(m.display_name || '?')[0]}</div>
                        ))}
                      </div>
                      <p className="text-[10px] text-[#6B6E73] font-medium">
                        Followed by {person.mutuals.sample[0]?.display_name?.split(' ')[0] || 'someone'}
                        {person.mutuals.count > 1 && ` + ${person.mutuals.count - 1} other${person.mutuals.count > 2 ? 's' : ''}`}
                      </p>
                    </div>
                  )}
                </div>
                {!person.is_self && (
                  <FollowButton
                    user={person}
                    testIdPrefix={`explore-${person.id}`}
                    onChange={(newStatus) => {
                      setPeople(prev => prev.map(p =>
                        p.id === person.id ? { ...p, is_following: newStatus } : p
                      ));
                    }}
                  />
                )}
              </div>
            ))}
            {people.length === 0 && (
              <div className="text-center py-16 px-6">
                <div className="w-16 h-16 rounded-full bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-4">
                  <Search size={28} className="text-[#3F5200]" />
                </div>
                <p className="text-base font-extrabold text-[#111] mb-1">
                  {query ? 'No matches yet' : 'Find your crew'}
                </p>
                <p className="text-sm text-[#6B6E73] leading-relaxed max-w-xs mx-auto">
                  {query
                    ? 'Try a different name, username, or interest.'
                    : 'Search by name, @username, or interest to discover people near you.'}
                </p>
              </div>
            )}
          </div>
        ) : tab === 'communities' ? (
          <div className="space-y-2 stagger-children">
            {communities.map(c => (
              <button
                key={c.id}
                data-testid={`community-card-${c.id}`}
                onClick={() => navigate(`/community/${c.id}`)}
                className="w-full flex items-center gap-3 bg-white rounded-2xl border border-black/[0.06] p-3 text-left active:bg-[#F1F0EA] transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#B6E600]/15 text-[#3F5200] flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {c.cover_image
                    ? <img src={c.cover_image} alt="" className="w-full h-full object-cover" />
                    : <UsersIcon size={20} strokeWidth={2.4} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-[#111] truncate">{c.name}</p>
                  <p className="text-[11px] text-[#6B6E73] truncate">
                    {(c.member_count ?? c.member_ids?.length ?? 0)} members
                    {c.category && ` · ${c.category}`}
                    {c.city && ` · ${c.city}`}
                  </p>
                </div>
              </button>
            ))}
            {communities.length === 0 && (
              <div className="text-center py-16 px-6">
                <div className="w-16 h-16 rounded-full bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-4">
                  <UsersIcon size={28} className="text-[#3F5200]" />
                </div>
                <p className="text-base font-extrabold text-[#111] mb-1">
                  {query ? 'No communities match' : 'No communities yet'}
                </p>
                <p className="text-sm text-[#6B6E73] leading-relaxed max-w-xs mx-auto">
                  Communities are long-lived tribes spun up by Community Leaders.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 stagger-children">
            {partners.map(p => <PartnerCard key={p.id} partner={p} />)}
            {partners.length === 0 && (
              <div className="text-center py-16 px-6">
                <div className="w-16 h-16 rounded-full bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-4">
                  <Store size={28} className="text-[#3F5200]" />
                </div>
                <p className="text-base font-extrabold text-[#111] mb-1">
                  {query || partnerType ? 'No partners match' : 'No partners yet here'}
                </p>
                <p className="text-sm text-[#6B6E73] leading-relaxed max-w-xs mx-auto">
                  Try a different category or search term.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <CommunityLockedSheet open={showLocked} onClose={() => setShowLocked(false)} />
      <CommunityCreateSheet
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(c) => { setShowCreate(false); navigate(`/community/${c.id}`); }}
      />
    </div>
  );
}
